import LNG_MAP from 'i18n/languages';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Config } from 'services/Config';
import StructuredData from './StructuredData';

const SUPPORTED_LOCALES = Object.freeze(Object.keys(LNG_MAP));

const normalizeLocale = (value, fallback = 'en') => {
  const normalized = typeof value === 'string' ? value.toLowerCase().trim() : '';
  if (SUPPORTED_LOCALES.includes(normalized)) {
    return normalized;
  }

  const normalizedFallback = typeof fallback === 'string' ? fallback.toLowerCase().trim() : '';
  return SUPPORTED_LOCALES.includes(normalizedFallback) ? normalizedFallback : 'en';
};

const normalizePath = (path) => {
  const raw = typeof path === 'string' ? path.trim() : '';
  if (!raw) {
    return '';
  }

  const withoutQuery = raw.split(/[?#]/)[0] || '';
  const cleaned = withoutQuery.replace(/^\/+|\/+$/g, '');
  if (!cleaned) {
    return '';
  }

  const pathParts = cleaned.split('/');
  const firstPart = (pathParts[0] || '').toLowerCase();
  if (SUPPORTED_LOCALES.includes(firstPart)) {
    return pathParts.slice(1).join('/');
  }

  return cleaned;
};

const buildLocalizedSeoUrl = (baseUrl, locale, path) => {
  const normalizedLocale = normalizeLocale(locale, 'en');
  const normalizedPath = normalizePath(path);
  const pathSuffix = normalizedPath ? `/${normalizedPath}` : '';
  return `${baseUrl}/${normalizedLocale}${pathSuffix}`;
};

const resolveLocalizedRoute = (pathname, fallbackPath, fallbackLocale) => {
  const locationPath = typeof pathname === 'string' ? pathname.trim() : '';
  const cleanedLocationPath = locationPath.replace(/^\/+|\/+$/g, '');
  const pathParts = cleanedLocationPath ? cleanedLocationPath.split('/') : [];
  const locationLocale = normalizeLocale(pathParts[0] || '', fallbackLocale);
  const hasLocalePrefix = SUPPORTED_LOCALES.includes((pathParts[0] || '').toLowerCase());

  if (hasLocalePrefix) {
    return {
      locale: locationLocale,
      path: pathParts.slice(1).join('/'),
    };
  }

  return {
    locale: normalizeLocale(fallbackLocale, 'en'),
    path: normalizePath(fallbackPath),
  };
};

const SEO = ({
  title,
  description,
  path,
  element,
  imagePath = "/og/mainpage-og.png",
  name = null,
  type = 'website',
  twitterCard = 'summary_large_image',
  noindex = false,
  structuredData = null,
}) => {
  const { t, i18n } = useTranslation("common");
  const location = useLocation();
  const fallbackLocale = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const resolvedRoute = resolveLocalizedRoute(location.pathname, path, fallbackLocale);
  const currentLocale = resolvedRoute.locale;
  const normalizedPath = resolvedRoute.path;
  const pathSuffix = normalizedPath ? `/${normalizedPath}` : '';
  const baseUrl = (Config.getBaseUrl() || 'https://randostat.com').replace(/\/+$/, '');
  const canonicalUrl = buildLocalizedSeoUrl(baseUrl, currentLocale, normalizedPath);
  const normalizedImagePath = typeof imagePath === 'string'
    ? imagePath.trim().replace(/^\/+/, '')
    : '';
  const imageUrl = normalizedImagePath
    ? `${baseUrl}/${normalizedImagePath}`
    : `${baseUrl}/og/mainpage-og.png`;

  return (
    <>
      <Helmet>
        { /* Standard metadata tags */}
        <title>{title}</title>
        <meta name="description" content={description} />
        {noindex ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : null}
        { /* End standard metadata tags */}

        { /* Facebook tags */}
        <meta property="og:type" content={type} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:secure_url" content={imageUrl} />
        <meta property="og:image:alt" content={title} />
        { /* End Facebook tags */}

        { /* Twitter tags */}
        <meta name="twitter:creator" content={name || t('global.fullName')} />
        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        { /* End Twitter tags */}

        {
          Object.keys(LNG_MAP).map((code) => {
            return (
              <link key={code} rel="alternate" href={`${baseUrl}/${code}${pathSuffix}`} hreflang={code} />
            )
          })
        }
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" href={`${baseUrl}/en${pathSuffix}`} hreflang="x-default" />
      </Helmet>
      <StructuredData data={structuredData} />
      {element}
    </>
  )
}

export default SEO;
